import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { EN_SERVICES } from '@/lib/enServices';
import { EN_CMS_PAIRS } from '@/lib/cmsLang';
import { enforceDescriptionLimit, enforceTitleLimit, HAIRSALON_SCHEMA, addJsonLd, clearJsonLd, getBreadcrumbSchema, getCanonicalUrl, getHomeFaqSchema, getTeamPersonSchema, isAdminRoute, seoConfig, updateMetaTags } from '@/lib/seo';

const SITE_URL = 'https://ikonomakis.gr';

// Remove ALL FAQPage JSON-LD scripts, including platform-auto-generated ones
const clearAllFaqSchemas = () => {
  document.querySelectorAll('script[type="application/ld+json"]').forEach((node) => {
    try {
      const data = JSON.parse(node.textContent);
      if (data?.['@type'] === 'FAQPage') node.remove();
    } catch {}
  });
};

// Genuine Greek ↔ English equivalents only. Greek is x-default.
const HREFLANG_PAIRS = {
  '/': '/en',
  '/services/face-metrics-pro': '/en/ai-hair-analysis',
  '/kommotirio-chalandri': '/en/english-speaking-hair-salon-athens',
  ...Object.fromEntries(EN_SERVICES.map((s) => [s.el, s.path])),
};
// English CMS twins (generated from data at build time). Hand-coded pairs above win.
const HAND_EN = new Set(Object.values(HREFLANG_PAIRS));
for (const [el, en] of Object.entries(EN_CMS_PAIRS)) {
  if (!HREFLANG_PAIRS[el] && !HAND_EN.has(en)) HREFLANG_PAIRS[el] = en;
}
// Legal pages own their head entirely (noindex, follow · no canonical · no JSON-LD).
const LEGAL_ROUTES = ['/privacy', '/terms', '/cookies', '/politiki-akyrosis'];
const EN_TO_EL = Object.fromEntries(Object.entries(HREFLANG_PAIRS).map(([el, en]) => [en, el]));

const addLink = (hreflang, href) => {
  const link = document.createElement('link');
  link.rel = 'alternate';
  link.hreflang = hreflang;
  link.href = href;
  document.head.appendChild(link);
};

const setHreflangTags = (pathname) => {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  const el = HREFLANG_PAIRS[pathname] ? pathname : EN_TO_EL[pathname];
  if (!el) return;
  addLink('el-GR', getCanonicalUrl(el));
  addLink('en', `${SITE_URL}${HREFLANG_PAIRS[el]}`);
  addLink('x-default', getCanonicalUrl(el));
};

export default function SEOHead() {
  const location = useLocation();

  // Safeguard: any page/CMS description > 160 chars is trimmed wherever it was set
  useEffect(() => {
    const enforce = () => { enforceDescriptionLimit(); enforceTitleLimit(); };
    enforce();
    const observer = new MutationObserver(enforce);
    observer.observe(document.head, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['content'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const pathname = location.pathname;
    const knownConfig = seoConfig[pathname];

    // Our own business graph and page schemas are authoritative; discard generated
    // duplicates and the unrelated software FAQ before crawler prerendering completes.
    document.querySelectorAll('script[type="application/ld+json"][data-seo-source="builder"]').forEach((node) => node.remove());
    document.documentElement.lang = pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'el';
    document.querySelector('meta[property="og:locale"]')?.setAttribute('content', pathname === '/en' || pathname.startsWith('/en/') ? 'en_GB' : 'el_GR');
    clearJsonLd();

    // Private area emits nothing public (ProtectedArea sets noindex itself).
    if (isAdminRoute(pathname)) return;

    if (LEGAL_ROUTES.includes(pathname)) {
      document.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((n) => n.remove());
      return;
    }

    addJsonLd(HAIRSALON_SCHEMA);
    setHreflangTags(pathname);

    // Dynamic routes (/:slug landing pages, Journal articles, /en/*) own their metadata.
    if (!knownConfig && pathname !== '/') return;

    const config = knownConfig || seoConfig['/'];
    updateMetaTags({
      ...config,
      url: getCanonicalUrl(pathname),
      noindex: config?.noindex === true,
      canonical: config?.canonical || null,
    });

    const breadcrumb = getBreadcrumbSchema(pathname);
    if (breadcrumb) addJsonLd(breadcrumb);

    if (pathname === '/') {
      clearAllFaqSchemas();
      addJsonLd(getHomeFaqSchema());
    }

    if (pathname === '/atelier/team') addJsonLd(getTeamPersonSchema());
  }, [location.pathname]);

  return null;
}